import axios, { AxiosResponse } from 'axios'
import { useEffect, useState } from 'react'
import { ChartEntry } from '../../../data/model/chart'
import {
  FiltersResponse,
  ForkFilter,
  SearchableFilterOption,
  SelectedFilter,
  WeightingScheme
} from '../../../data/model/results'
import requestWithRetry from '../../../utils/requestWithRetry'
import {
  mapPublicFilters,
  transformForkDataIntoSearchableFilterOption
} from '../../../utils/resultsUtils'
import { PublicResultsApiRouting } from '../publicApiSource'

interface SummaryVars {
  surveyId: string
  filters?: SelectedFilter[]
  apiRouting: PublicResultsApiRouting | undefined
}

interface ResponseData {
  results: ChartEntry[]
  weightingScheme: WeightingScheme
}
interface SummaryData {
  loading: boolean
  error: boolean
  weightingScheme: WeightingScheme | undefined
  data: ChartEntry[]
}

export const usePublicSummary = ({
  surveyId,
  filters,
  apiRouting
}: SummaryVars): SummaryData => {
  const [error, setError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [weightingScheme, setWeightingScheme] = useState<WeightingScheme>()
  const [data, setData] = useState<ChartEntry[]>([])

  useEffect(() => {
    if (apiRouting == null) {
      // Routing is cleared while metadata resolves for a new survey token, so
      // drop the previous survey's results rather than rendering them again.
      setData([])
      setWeightingScheme(undefined)
      setError(false)
      return
    }

    setLoading(true)

    const responseFilters =
      filters?.filter((filter) => !!filter.questionId) || []
    const forkFilters =
      filters
        ?.filter((filter) => !!filter.forkId)
        .map((fl) => {
          return {
            ...fl,
            branchNumbers: fl.branchNumbers?.map((branchNumber) =>
              Number.parseInt(branchNumber, 10)
            )
          }
        }) || []

    const postPayload = {
      surveyId,
      responseFilters,
      forkFilters
    }

    requestWithRetry({
      method: 'post',
      url: `${apiRouting.baseUrl}${apiRouting.resultsEndpoint}`,
      payload: postPayload
    })
      .then((response: AxiosResponse<ResponseData>) => {
        if (response.status >= 200 && response.status < 400) {
          setError(false)
          setData(response.data.results)
          setWeightingScheme(response.data.weightingScheme)
        } else {
          setError(true)
        }
      })
      .catch((requestError) => {
        if (!axios.isCancel(requestError)) {
          setError(true)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }, [surveyId, filters, apiRouting])

  return { loading, error, weightingScheme, data }
}

interface FilterVars {
  surveyId: string
  questionIds?: string[]
  apiRouting: PublicResultsApiRouting | undefined
}

interface LegacyFilterResponseData {
  filters: SearchableFilterOption[]
  forkFilters: ForkFilter[]
}

interface FilterData {
  error: boolean
  loading: boolean
  data: SearchableFilterOption[]
}

const FILTERS_ENDPOINT = '/filters'

export const usePublicFilters = ({
  surveyId,
  questionIds = [],
  apiRouting
}: FilterVars): FilterData => {
  const [error, setError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [data, setData] = useState<SearchableFilterOption[]>([])
  const query = new URLSearchParams()
  // Legacy cin-public-results does not support selecting question counts.
  if (apiRouting?.version === 'v2') {
    questionIds.forEach((id) => query.append('questionIds', id))
  }
  const queryString = query.toString()

  useEffect(() => {
    if (apiRouting == null) {
      setData([])
      setError(false)
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)

    requestWithRetry({
      method: 'get',
      url: `${apiRouting.baseUrl}${FILTERS_ENDPOINT}/${surveyId}${
        queryString ? `?${queryString}` : ''
      }`
    })
      .then(
        (
          response: AxiosResponse<FiltersResponse | LegacyFilterResponseData>
        ) => {
          if (!active) {
            return
          }
          if (response.status >= 200 && response.status < 400) {
            setError(false)
            if (apiRouting.usesNativeFilterMapping) {
              setData(mapPublicFilters(response.data as FiltersResponse))
            } else {
              const legacyData = response.data as LegacyFilterResponseData
              setData([
                ...transformForkDataIntoSearchableFilterOption(
                  legacyData.forkFilters
                ),
                ...legacyData.filters
              ])
            }
          } else {
            setError(true)
          }
        }
      )
      .catch((requestError) => {
        if (active && !axios.isCancel(requestError)) {
          setError(true)
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false)
        }
      })
    // Changing displayed cards can overlap requests; ignore outdated counts.
    return () => {
      active = false
    }
  }, [surveyId, apiRouting, queryString])

  return { data, error, loading }
}
